import React, { useState, useRef, useEffect } from "react";
import service from "../../../services/service";
import { Invit } from "../../../types/invit.types";
import "./table.less";
import { useParameters } from "../../../context/parametersContext";
import { formatDisplayValue } from "../../../utils/financialHelper";

interface OperationalData {
  metric: string;
  values: { [key: string]: string | number };
}

interface ProjectData {
  name: string;
  color: string;
  sector: string;
  data: OperationalData[];
}

const OperationalMetricsTable: React.FC = () => {
  const [selectedSector, setSelectedSector] = useState<string>("Roads");
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [selectedYear, setSelectedYear] = useState<string>("FY 2024-25");
  const [selectedQuarters, setSelectedQuarters] = useState<string[]>(["Q4"]);
  const [showSectorDropdown, setShowSectorDropdown] = useState(false);
  const [showQuarterDropdown, setShowQuarterDropdown] = useState(false);
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [allProjects, setAllProjects] = useState<ProjectData[]>([]);
  const [projectsData, setProjectsData] = useState<{
    [key: string]: ProjectData;
  }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const addProjectModalRef = useRef<HTMLDivElement>(null);
  const quarterDropdownRef = useRef<HTMLDivElement>(null);
  const yearDropdownRef = useRef<HTMLDivElement>(null);
  const sectorDropdownRef = useRef<HTMLDivElement>(null);
  const [availableQuarters, setAvailableQuarters] = useState<string[]>([]);

  const { activeToggle } = useParameters();
  const isPublic = activeToggle === "Public";

  const availableSectors = isPublic
    ? ["Roads", "Power"] // PUBLIC
    : [
        "Roads",
        "Power",
        "Telecommunication",
        "Oil & Gas",
        "Renewable",
        "Energy",
      ]; // PRIVATE

  const availableYears = [
    "FY 2024-25",
    "FY 2023-24",
    "FY 2022-23",
    "FY 2018-19",
    "FY 2017-18",
  ];

  const metricDisplayNames: { [key: string]: string } = {
    totalRevenueFromOperations: "Total Revenue from Operations",
    sectorOrSubSectorWiseRevenueSplit: "Sector / Sub-sector wise Revenue Split",
    ebitda: "EBITDA",
    ebitdaMargin: "EBITDA Margin (%)",
    operationsOandMexpenses: "O&M Expenses",
    capex: "Capex",
    assetsUnderManagementOrEnterpriseValue:
      "Assets Under Management (AUM) or Enterprise Value (EV)",
    marketCapitalisation: "Market Capitalisation",
    nav: "NAV/ unit (Fair Value) (₹ p.u.)",
    ndfcAtInvitQuarterly: "NDCF of InvIT (Quarterly)",
    ndcfAtInvitYTD: "NDCF of InvIT (YTD)",
    grossBorrowing: "Gross Borrowing",
    costOfDebt: "Cost of Debt (% p.a.)",
    equityRaisedTillDate: "Equity Raised Till Date",
    creditRating: "Credit Rating (from all agencies)",
    distributionPerUnitQuarterly:
      "Total Distribution per Unit (Quarterly) (₹ p.u.)",
    distributionPerUnitYTD: "Total Distribution per Unit (YTD) (₹ p.u.)",
    netDebtOrAum: "Net Debt/ AUM",
    nameOfProject: "Name of the Project",
    natureOfProject: "Nature of the Project (HAM/Toll)",
    statusOfProject: "Status of the Project (Operational/Under Construction)",
    counterparty: "Counterparty",
    wacc: "WACC (%)",
    enterpriseValue: "Enterprise Value",
    totalLengthOfRoads: "Total Length of Roads (kms)",
    totalLaneKms: "Total Lane Kms",
    geographicalPresence: "Geographical Presence (Name of State)",
    concessionLifeRemaining: "Concession Life Remaining (in yrs)",
    tollRevenue: "Toll Revenue",
    annuityRevenue:
      "Annuity Revenue (including interest on annuity and O&M income)",
    balanceAnnuitiesReceivable: "Balance Annuities Receivable",
    totalNumberOfAnnuitiesReceived:
      "Total no of Annuities Received (A out of 30)",
    typeOfProject: "Type of Project",
    transmissionLineLength: "Transmission Line Length",
    numberOfTowers: "Number of Towers",
    transformationCapacity: "Transformation Capacity",
    generationCapacity: "Generation Capacity",
    storageCapacity: "Storage Capacity",
    contractLifeRemaining: "Contract Life Remaining",
    blendedAvailability: "Blended Availability",
    cuf: "CUF",
  };

  useEffect(() => {
    fetchInvitsData();
  }, [activeToggle]);

  const extractMetricValue = (
    obj: any,
    key: string,
    period: string,
    metricsMap: any
  ) => {
    const displayName = metricDisplayNames[key] || key;

    if (!metricsMap[displayName]) {
      metricsMap[displayName] = {};
    }

    if (obj && typeof obj === "object" && "value" in obj) {
      metricsMap[displayName][period] =
        obj.value != null ? String(obj.value) : "—";
    }
  };

  const fetchInvitsData = async () => {
    try {
      setLoading(true);

      const isPublic = activeToggle === "Public";

      const response: Invit[] = await service.getInvits(isPublic);

      const projectsDataMap: {
        [key: string]: {
          name: string;
          sector: string;
          color: string;
          metricsMap: {
            [metricName: string]: { [period: string]: string | number };
          };
        };
      } = {};

      response.forEach((invit) => {
        if (invit.operationalMetrics && invit.operationalMetrics.length > 0) {
          invit.operationalMetrics.forEach((yearData) => {
            const year = yearData.year || "FY 2024";

            if (yearData.semiAnnuals && yearData.semiAnnuals.length > 0) {
              yearData.semiAnnuals.forEach((semiAnnualData) => {
                const sectorOp = semiAnnualData.sectorOfOperation;

                if (!sectorOp) return;

                const projectName = sectorOp?.nameOfProject?.value as string;
                const sectorName = sectorOp?.name || "Unknown";
                const quarter = semiAnnualData.semiAnnual || "Q1";
                const period = `${year} ${quarter}`;

                if (!projectName) return;

                if (!projectsDataMap[projectName]) {
                  projectsDataMap[projectName] = {
                    name: projectName,
                    sector: sectorName,
                    color: "#2FBEB7",
                    metricsMap: {},
                  };
                }

                const metricsMap = projectsDataMap[projectName].metricsMap;

                Object.entries(sectorOp).forEach(([key, value]) => {
                  if (key === "name" || key === "_id") return;

                  if (key === "revenue" && value && typeof value === "object") {
                    Object.entries(value).forEach(([revKey, revValue]) => {
                      extractMetricValue(revValue, revKey, period, metricsMap);
                    });
                  } else if (
                    key === "capacity" &&
                    value &&
                    typeof value === "object"
                  ) {
                    Object.entries(value).forEach(([capKey, capValue]) => {
                      extractMetricValue(capValue, capKey, period, metricsMap);
                    });
                  } else if (
                    key === "blendedAvailabilityOrCUF" &&
                    value &&
                    typeof value === "object"
                  ) {
                    Object.entries(value).forEach(([blendKey, blendValue]) => {
                      if (blendKey === "key" || blendKey === "value") return;
                      extractMetricValue(
                        blendValue,
                        blendKey,
                        period,
                        metricsMap
                      );
                    });
                  } else {
                    extractMetricValue(value, key, period, metricsMap);
                  }
                });
              });
            }
          });
        }
      });

      const finalProjectsData: { [key: string]: ProjectData } = {};
      const allProjectsList: ProjectData[] = [];

      Object.values(projectsDataMap).forEach((projectInfo) => {
        const dataArray: OperationalData[] = Object.entries(
          projectInfo.metricsMap
        ).map(([metric, values]) => ({
          metric,
          values,
        }));

        const projectData: ProjectData = {
          name: projectInfo.name,
          color: projectInfo.color,
          sector: projectInfo.sector,
          data: dataArray,
        };

        finalProjectsData[projectInfo.name] = projectData;
        allProjectsList.push(projectData);
      });

      const extractedQuarters = new Set<string>();

      response.forEach((invit) => {
        invit.operationalMetrics?.forEach((year) => {
          year.semiAnnuals?.forEach((semi) => {
            if (semi.semiAnnual) {
              extractedQuarters.add(semi.semiAnnual);
            }
          });
        });
      });

      const uniqueQuarters = Array.from(extractedQuarters);
      setAvailableQuarters(uniqueQuarters);

      setAllProjects(allProjectsList);
      setProjectsData(finalProjectsData);

      const defaultSectorProjects = allProjectsList.filter(
        (p) => p.sector === "Roads"
      );
      if (defaultSectorProjects.length > 0) {
        setSelectedProjects([defaultSectorProjects[0].name]);
      }

      setLoading(false);
    } catch (err) {
      setError("Failed to fetch InvIT data");
      setLoading(false);
      console.error("Error fetching InvITs:", err);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        quarterDropdownRef.current &&
        !quarterDropdownRef.current.contains(event.target as Node)
      ) {
        setShowQuarterDropdown(false);
      }
      if (
        yearDropdownRef.current &&
        !yearDropdownRef.current.contains(event.target as Node)
      ) {
        setShowYearDropdown(false);
      }
      if (
        addProjectModalRef.current &&
        !addProjectModalRef.current.contains(event.target as Node)
      ) {
        setShowAddProjectModal(false);
      }
      if (
        sectorDropdownRef.current &&
        !sectorDropdownRef.current.contains(event.target as Node)
      ) {
        setShowSectorDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredProjects = allProjects.filter(
    (project) => project.sector === selectedSector
  );

  const metrics =
    selectedProjects.length > 0 && projectsData[selectedProjects[0]]
      ? projectsData[selectedProjects[0]].data.map((d) => d.metric)
      : [];

  const removeProject = (projectName: string) => {
    if (selectedProjects.length > 1) {
      setSelectedProjects(
        selectedProjects.filter((name) => name !== projectName)
      );
    }
  };

  const addProject = (projectName: string) => {
    if (
      selectedProjects.length < 4 &&
      !selectedProjects.includes(projectName)
    ) {
      setSelectedProjects([...selectedProjects, projectName]);
      setShowAddProjectModal(false);
    }
  };

  const toggleQuarter = (quarter: string) => {
    if (selectedQuarters.includes(quarter)) {
      if (selectedQuarters.length > 1) {
        setSelectedQuarters(selectedQuarters.filter((q) => q !== quarter));
      }
    } else {
      if (selectedQuarters.length < 4) {
        const newQuarters = [...selectedQuarters, quarter].sort((a, b) => {
          const qNumA = parseInt(a.replace("Q", ""));
          const qNumB = parseInt(b.replace("Q", ""));
          return qNumA - qNumB;
        });
        setSelectedQuarters(newQuarters);
      }
    }
  };

  const handleYearChange = (year: string) => {
    setSelectedYear(year);
    setShowYearDropdown(false);
  };

  const handleSectorChange = (sector: string) => {
    setSelectedSector(sector);
    setSelectedProjects([]);
    setShowSectorDropdown(false);
  };

  const periods = selectedQuarters.map((q) => `${selectedYear} ${q}`);

  const removeQuarter = (quarter: string) => {
    if (selectedQuarters.length > 1) {
      setSelectedQuarters(selectedQuarters.filter((q) => q !== quarter));
    }
  };

  if (loading) {
    return (
      <div className="operational-table">
        <div style={{ padding: "40px", textAlign: "center" }}>
          Loading InvIT data...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="operational-table">
        <div style={{ padding: "40px", textAlign: "center", color: "red" }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="operational-table">
      <div className="operational-table__header-section">
        <div className="operational-table__title-section">
          <h3>
            Compare <br />
            <span>InvITs Operational Metrics</span>
          </h3>
        </div>
        <div className="operational-table__controls">
          <div className="operational-table__selected-quarters">
            {selectedQuarters.map((quarter) => (
              <div key={quarter} className="operational-table__quarter-chip">
                <span>{quarter}</span>
                {selectedQuarters.length > 1 && (
                  <button
                    className="operational-table__quarter-chip-remove"
                    onClick={() => removeQuarter(quarter)}
                    aria-label={`Remove ${quarter}`}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="operational-table__right-controls">
            <div
              className="operational-table__dropdown-wrapper"
              ref={sectorDropdownRef}
            >
              <button
                className="operational-table__selector-btn"
                onClick={() => {
                  setShowSectorDropdown(!showSectorDropdown);
                  setShowQuarterDropdown(false);
                  setShowYearDropdown(false);
                }}
              >
                <span className="operational-table__btn-text">
                  {selectedSector}
                </span>
                <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                  <path
                    d="M1 1.5L6 6.5L11 1.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {showSectorDropdown && (
                <div className="operational-table__dropdown">
                  {availableSectors.map((sector) => (
                    <label
                      key={sector}
                      className="operational-table__dropdown-item"
                    >
                      <input
                        type="radio"
                        checked={selectedSector === sector}
                        onChange={() => handleSectorChange(sector)}
                      />
                      <span>{sector}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div
              className="operational-table__dropdown-wrapper"
              ref={quarterDropdownRef}
            >
              <button
                className="operational-table__selector-btn operational-table__selector-btn--with-label"
                onClick={() => {
                  setShowQuarterDropdown(!showQuarterDropdown);
                  setShowYearDropdown(false);
                  setShowSectorDropdown(false);
                }}
              >
                <span className="operational-table__btn-label">
                  Select Quarter
                </span>
                <span className="operational-table__btn-value">
                  {selectedQuarters[selectedQuarters.length - 1]}
                </span>
                <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                  <path
                    d="M1 1.5L6 6.5L11 1.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {showQuarterDropdown && (
                <div className="operational-table__dropdown">
                  {availableQuarters.length > 0 ? (
                    availableQuarters.map((quarter) => (
                      <label
                        key={quarter}
                        className="operational-table__dropdown-item"
                      >
                        <input
                          type="checkbox"
                          checked={selectedQuarters.includes(quarter)}
                          onChange={() => toggleQuarter(quarter)}
                          disabled={
                            !selectedQuarters.includes(quarter) &&
                            selectedQuarters.length >= 4
                          }
                        />
                        <span>{quarter}</span>
                      </label>
                    ))
                  ) : (
                    <div className="operational-table__dropdown-item">
                      No quarters available
                    </div>
                  )}
                </div>
              )}
            </div>

            <div
              className="operational-table__add-project-wrapper"
              ref={addProjectModalRef}
            >
              <button
                className="operational-table__selector-btn operational-table__add-project-btn"
                onClick={() => setShowAddProjectModal(!showAddProjectModal)}
                disabled={!selectedSector}
                style={{ opacity: !selectedSector ? 0.5 : 1 }}
              >
                <span className="operational-table__btn-text">Add Project</span>
                <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                  <path
                    d="M1 1.5L6 6.5L11 1.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {showAddProjectModal && (
                <div className="operational-table__project-modal">
                  <div className="operational-table__project-modal-header">
                    <h3>Select Project</h3>
                    <p>Choose up to 4 projects to compare</p>
                  </div>
                  <div className="operational-table__project-modal-body">
                    {filteredProjects.map((project) => {
                      const isSelected = selectedProjects.includes(
                        project.name
                      );
                      const projectColor = project.color;

                      return (
                        <div
                          key={project.name}
                          className={`operational-table__project-card ${
                            isSelected
                              ? "operational-table__project-card--selected"
                              : ""
                          } ${
                            !isSelected && selectedProjects.length >= 4
                              ? "operational-table__project-card--disabled"
                              : ""
                          }`}
                          onClick={() => {
                            if (!isSelected && selectedProjects.length < 4) {
                              addProject(project.name);
                            }
                          }}
                          style={{
                            opacity: isSelected ? 0.5 : 1,
                            pointerEvents: isSelected ? "none" : "auto",
                          }}
                        >
                          <div
                            className="operational-table__project-card-indicator"
                            style={{ backgroundColor: projectColor }}
                          />
                          <div className="operational-table__project-card-content">
                            <span className="operational-table__project-card-name">
                              {project.name}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div
              className="operational-table__dropdown-wrapper"
              ref={yearDropdownRef}
            >
              <button
                className="operational-table__selector-btn operational-table__selector-btn--year"
                onClick={() => {
                  setShowYearDropdown(!showYearDropdown);
                  setShowQuarterDropdown(false);
                  setShowSectorDropdown(false);
                }}
              >
                <span className="operational-table__btn-text">
                  {selectedYear}
                </span>
                <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                  <path
                    d="M1 1.5L6 6.5L11 1.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {showYearDropdown && (
                <div className="operational-table__dropdown">
                  {availableYears.map((year) => (
                    <label
                      key={year}
                      className="operational-table__dropdown-item"
                    >
                      <input
                        type="radio"
                        checked={selectedYear === year}
                        onChange={() => handleYearChange(year)}
                      />
                      <span>{year}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="operational-table__container">
        <table className="operational-table__table">
          <thead>
            <tr>
              <th className="operational-table__header-metric">
                Operational Metrics
              </th>
              {selectedProjects.map((projectName) => (
                <th
                  key={projectName}
                  className="operational-table__header-project"
                  colSpan={periods.length}
                  style={{ backgroundColor: projectsData[projectName]?.color }}
                >
                  <div className="operational-table__project-header">
                    <span>{projectName}</span>
                    {selectedProjects.length > 1 && (
                      <button
                        className="operational-table__remove-btn"
                        onClick={() => removeProject(projectName)}
                        aria-label={`Remove ${projectName}`}
                      >
                        ×
                      </button>
                    )}
                  </div>
                </th>
              ))}
            </tr>
            <tr>
              <th className="operational-table__subheader"></th>
              {selectedProjects.map((projectName) =>
                periods.map((period) => (
                  <th
                    key={`${projectName}-${period}`}
                    className="operational-table__subheader"
                  >
                    {period.split(" ").pop()}
                  </th>
                ))
              )}
            </tr>
          </thead>
          <tbody>
            {metrics.map((metric, index) => (
              <tr key={index} className="operational-table__row">
                <td className="operational-table__metric-name">{metric}</td>
                {selectedProjects.map((projectName) => {
                  const projectData = projectsData[projectName];
                  const metricData = projectData?.data.find(
                    (d) => d.metric === metric
                  );
                  return periods.map((period) => {
                    const value = metricData?.values[period];
                    return (
                      <td
                        key={`${projectName}-${period}`}
                        className="operational-table__value"
                      >
                        {formatDisplayValue(value)}
                      </td>
                    );
                  });
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OperationalMetricsTable;
